// Savings & activity dashboard for the consignment-merging features
// (add box to existing consignment, cross-channel add box, consolidate
// orders). Shows boxes added / orders consolidated + money saved over
// 1 / 7 / 30 / 60-day and all-time windows. Read-only; opened from the
// APC carrier card.

import { useEffect, useState } from "react";
import { PiggyBank, X } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";

type WindowKey = "1d" | "7d" | "30d" | "60d" | "all";
type Cell = {
    count: number;
    saving: number;
    // Units merged, and what the total is made of. Optional so a BE that
    // predates the packaging/handling components still renders.
    units?: number;
    breakdown?: { carriage: number; packaging: number; handling: number };
};
interface Feature {
    key: string;
    unit: "boxes" | "orders";
    enabled: boolean;
    estimated?: boolean;
    windows: Record<WindowKey, Cell>;
}
interface SavingsResponse {
    windows: WindowKey[];
    savingPerEliminatedShipment: number;
    savingPerBoxPackaging?: number;
    savingPerItemHandling?: number;
    features: Feature[];
}

const gbp = (n: number) =>
    "£" + (Math.round(n * 100) / 100).toLocaleString(undefined, {
        minimumFractionDigits: n % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    });

interface Props {
    onClose: () => void;
}

const CarrierSavingsModal = ({ onClose }: Props) => {
    const { t } = useTranslation();
    const [data, setData] = useState<SavingsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const res = await apiFetch("/admin/carriers/savings");
                const json = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
                if (live) setData(json as SavingsResponse);
            } catch (err) {
                if (live) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => { live = false; };
    }, []);

    const windows: WindowKey[] = data?.windows ?? ["1d", "7d", "30d", "60d", "all"];
    const anyEstimated = data?.features.some((f) => f.estimated);

    // Money saved across every feature, per window — the table's totals
    // row. Counts are summed per UNIT (boxes vs orders) and never added
    // together: 40 boxes and 148 orders are not 188 of anything.
    const totals = windows.map((w) => {
        let saving = 0, boxes = 0, orders = 0;
        for (const f of data?.features ?? []) {
            const cell = f.windows?.[w];
            if (!cell) continue;
            saving += cell.saving ?? 0;
            if (f.unit === "boxes") boxes += cell.count ?? 0;
            else orders += cell.count ?? 0;
        }
        return { key: w, saving, boxes, orders };
    });

    // The headline reads off the same totals so it can never disagree
    // with the "All time" column below it.
    const totalAllTime = totals.find((t) => t.key === "all")?.saving ?? 0;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-card carrier-savings-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="carrier-savings-title"
            >
                <div className="modal-card-header">
                    <h2 id="carrier-savings-title">
                        <PiggyBank size={18} style={{ marginRight: 8, verticalAlign: "-3px" }} />
                        {t("savings_title")}
                    </h2>
                    <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>

                <div className="modal-card-body">
                    {loading && <p>{t("loading")}</p>}
                    {error && <p className="admin-integrations-field-error">{error}</p>}

                    {data && (
                        <>
                            <div className="carrier-savings-headline">
                                <span className="carrier-savings-headline-value">{gbp(totalAllTime)}</span>
                                <span className="carrier-savings-headline-label">{t("savings_headline_all_time")}</span>
                            </div>

                            <div className="carrier-savings-scroll">
                                <table className="carrier-savings-table">
                                    <thead>
                                        <tr>
                                            <th>{t("savings_feature_col")}</th>
                                            {windows.map((w) => (
                                                <th key={w} className="carrier-savings-num">{t(`savings_period_${w}`)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.features.map((f) => (
                                            <tr key={f.key}>
                                                <td>
                                                    <span className="carrier-savings-feature-name">
                                                        {t(`savings_feature_${f.key}`)}
                                                    </span>
                                                    {!f.enabled && (
                                                        <span className="carrier-savings-off">{t("savings_feature_off")}</span>
                                                    )}
                                                </td>
                                                {windows.map((w) => {
                                                    const cell = f.windows?.[w] ?? { count: 0, saving: 0 };
                                                    return (
                                                        <td key={w} className="carrier-savings-num">
                                                            <span className="carrier-savings-money">{gbp(cell.saving)}</span>
                                                            <span className="carrier-savings-count">
                                                                {t(`savings_unit_${f.unit}`, { n: cell.count.toLocaleString() })}
                                                            </span>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="carrier-savings-total-row">
                                            <td>
                                                <span className="carrier-savings-feature-name">
                                                    {t("savings_total_row")}
                                                </span>
                                            </td>
                                            {totals.map((tot) => (
                                                <td key={tot.key} className="carrier-savings-num">
                                                    <span className="carrier-savings-money">{gbp(tot.saving)}</span>
                                                    <span className="carrier-savings-count">
                                                        {[
                                                            t("savings_unit_boxes", { n: tot.boxes.toLocaleString() }),
                                                            t("savings_unit_orders", { n: tot.orders.toLocaleString() }),
                                                        ].join(" · ")}
                                                    </span>
                                                </td>
                                            ))}
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {anyEstimated && (
                                <p className="carrier-savings-note">
                                    {t("savings_estimate_note", {
                                        rate: gbp(data.savingPerEliminatedShipment),
                                        packaging: gbp(data.savingPerBoxPackaging ?? 0),
                                        handling: gbp(data.savingPerItemHandling ?? 0),
                                    })}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CarrierSavingsModal;
